import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ArrowLeft, Users, CheckCircle, X, ChevronRight, DollarSign,
  Calendar, Search, Settings, ChevronDown, ChevronUp, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const FLOW_COLORS = {
  External: "text-[#2F5D8A] bg-[#2F5D8A]/12 border-[#2F5D8A]/25",
  Internal: "text-[#B08A3E] bg-[#D1A054]/15 border-[#D1A054]/30",
  Regular: "text-[#2F5D8A] bg-[#2F5D8A]/12 border-[#2F5D8A]/25",
};

const TransmissionPage = ({ onBack }) => {
  const [view, setView] = useState("today"); // today | history | weekly | rates | office
  const [dailyData, setDailyData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [officeData, setOfficeData] = useState(null);
  const [rates, setRates] = useState({ external: 5, internal: 5, regular: 5 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Rates editing
  const [editRates, setEditRates] = useState({ external: 5, internal: 5, regular: 5 });
  const [savingRates, setSavingRates] = useState(false);

  // Date selection for daily view
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });

  const fetchDaily = useCallback(async (date) => {
    try {
      const res = await axios.get(`${API}/transmissions/daily`, { params: { date } });
      setDailyData(res.data);
    } catch (e) {
      console.error("Failed to fetch daily:", e);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transmissions/rates`);
      setRates(res.data);
      setEditRates(res.data);
    } catch (e) {
      console.error("Failed to fetch rates:", e);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transmissions/history`, { params: { days: 14 } });
      setHistoryData(res.data);
    } catch (e) {
      console.error("Failed to fetch history:", e);
    }
  }, []);

  const fetchWeekly = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transmissions/inspector-summary`, { params: { weeks: 4 } });
      setWeeklyData(res.data);
    } catch (e) {
      console.error("Failed to fetch weekly:", e);
    }
  }, []);

  const fetchOfficePaid = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transmissions/office-paid`, { params: { days: 30 } });
      setOfficeData(res.data);
    } catch (e) {
      console.error("Failed to fetch office-paid:", e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchDaily(selectedDate), fetchRates()]);
      setLoading(false);
    };
    load();
  }, [fetchDaily, fetchRates, selectedDate]);

  useEffect(() => {
    if (view === "history") fetchHistory();
    if (view === "weekly") fetchWeekly();
    if (view === "office") fetchOfficePaid();
  }, [view, fetchHistory, fetchWeekly, fetchOfficePaid]);

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      const res = await axios.put(`${API}/transmissions/rates`, editRates);
      setRates(res.data);
      setMessage({ type: "success", text: "Rates updated" });
      fetchDaily(selectedDate);
    } catch (e) {
      setMessage({ type: "error", text: "Failed to update rates" });
    } finally {
      setSavingRates(false);
    }
  };

  const handleRefresh = () => {
    fetchDaily(selectedDate);
    if (view === "history") fetchHistory();
    if (view === "weekly") fetchWeekly();
    if (view === "office") fetchOfficePaid();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" />
      </div>
    );
  }

  const navButtons = [
    { id: "today", label: "Today", icon: Calendar },
    { id: "history", label: "History", icon: Calendar },
    { id: "weekly", label: "Weekly Summary", icon: DollarSign },
    { id: "office", label: "Office-Paid", icon: DollarSign },
    { id: "rates", label: "Rates", icon: Settings },
  ];

  return (
    <div className="space-y-5" data-testid="transmission-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-[#1F2937]" data-testid="transmission-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Transmission</h1>
            <p className="text-gray-500 text-sm">Live record of transmitted orders per inspector</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1F2937] transition-colors"
          data-testid="transmission-refresh"
          title="Refresh data"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
          {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <X className="h-4 w-4" />}
          <span className="text-sm">{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-[#2F5D8A]/5 border border-[#2F5D8A]/15 rounded-lg px-4 py-2.5 text-xs text-[#2F5D8A]">
        Only inspectors with orders marked "Transmitted by Office" appear here. Use <span className="font-semibold">Inspector Availability</span> in Dispatch to transmit orders.
      </div>

      {/* Nav Tabs */}
      <div className="flex flex-wrap gap-1.5">
        {navButtons.map((btn) => {
          const Icon = btn.icon;
          return (
            <button
              key={btn.id}
              onClick={() => setView(btn.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-all ${view === btn.id ? "bg-[#D1A054] text-[#162d4a]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`transmission-tab-${btn.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {btn.label}
            </button>
          );
        })}
      </div>

      {/* ═══ TODAY VIEW ═══ */}
      {view === "today" && (
        <TodayView
          dailyData={dailyData}
          selectedDate={selectedDate}
          setSelectedDate={(d) => { setSelectedDate(d); fetchDaily(d); }}
          rates={rates}
        />
      )}

      {/* ═══ HISTORY VIEW ═══ */}
      {view === "history" && <HistoryView historyData={historyData} />}

      {/* ═══ WEEKLY SUMMARY VIEW ═══ */}
      {view === "weekly" && <WeeklyView weeklyData={weeklyData} />}

      {/* ═══ RATES VIEW ═══ */}
      {view === "rates" && (
        <RatesView
          editRates={editRates}
          setEditRates={setEditRates}
          onSave={handleSaveRates}
          saving={savingRates}
        />
      )}

      {view === "office" && <OfficePaidView officeData={officeData} />}
    </div>
  );
};

// ──────── SUB-VIEWS ────────

const TodayView = ({ dailyData, selectedDate, setSelectedDate, rates }) => {
  if (!dailyData) return null;
  const { inspectors: inspList, day_total } = dailyData;

  return (
    <div className="space-y-4" data-testid="transmission-today">
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none"
          data-testid="transmission-date-picker"
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-400">Day Total:</span>
          <span className="text-lg font-bold text-[#6B8F62]" data-testid="transmission-day-total">$ {day_total.toFixed(2)}</span>
        </div>
      </div>

      {/* Rates bar */}
      <div className="flex gap-3 text-[10px] uppercase text-gray-400">
        <span>External: <span className="text-[#2F5D8A] font-medium">$ {rates.external}</span></span>
        <span>Internal: <span className="text-[#B08A3E] font-medium">$ {rates.internal}</span></span>
        <span>Regular: <span className="text-[#2F5D8A] font-medium">$ {rates.regular}</span></span>
      </div>

      {inspList.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-lg border border-gray-200">
          No transmissions recorded for this date
        </div>
      ) : (
        <div className="space-y-3">
          {inspList.map((insp) => (
            <InspectorCard key={insp.inspector_name} insp={insp} rates={rates} />
          ))}
        </div>
      )}
    </div>
  );
};

const InspectorCard = ({ insp, rates }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`transmission-inspector-card-${insp.inspector_name}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-750"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
            <Users className="h-4 w-4 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1F2937]">{insp.inspector_name}</p>
            <p className="text-[10px] text-gray-400">{insp.total_orders} orders transmitted</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {insp.external > 0 && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${FLOW_COLORS.External}`}>EXT {insp.external}</span>}
            {insp.internal > 0 && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${FLOW_COLORS.Internal}`}>INT {insp.internal}</span>}
            {insp.regular > 0 && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${FLOW_COLORS.Regular}`}>REG {insp.regular}</span>}
          </div>
          <span className="text-sm font-bold text-[#6B8F62]" data-testid={`transmission-inspector-amount-${insp.inspector_name}`}>$ {insp.total_amount.toFixed(2)}</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {expanded && insp.orders && (
        <div className="border-t border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">Order #</th>
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">Client</th>
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">Type</th>
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">Value</th>
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">By</th>
                <th className="px-4 py-1.5 text-left text-gray-400 font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {insp.orders.map((o, i) => {
                const flowKey = (o.flow_type || "Regular").toLowerCase();
                const orderValue = rates ? (rates[flowKey] || rates.regular || 5) : 5;
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-1.5 text-gray-800 font-medium">{o.order_number}</td>
                    <td className="px-4 py-1.5 text-gray-600">{o.cliente}</td>
                    <td className="px-4 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${FLOW_COLORS[o.flow_type] || FLOW_COLORS.Regular}`}>
                        {o.flow_type}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 text-[#6B8F62] font-medium">$ {orderValue.toFixed(2)}</td>
                    <td className="px-4 py-1.5 text-gray-500">{o.transmitted_by}</td>
                    <td className="px-4 py-1.5 text-gray-400">{o.created_at ? new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const HistoryView = ({ historyData }) => {
  if (!historyData || historyData.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-lg border border-gray-200" data-testid="transmission-history">
        No transmission history found
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="transmission-history">
      {historyData.map((day) => (
        <div key={day.date} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-[#1F2937]">{day.date}</span>
              <span className="text-xs text-gray-400">{day.total_orders} orders</span>
            </div>
            <span className="text-sm font-bold text-[#6B8F62]">$ {day.day_total.toFixed(2)}</span>
          </div>
          <div className="space-y-1.5">
            {day.inspectors.map((insp) => (
              <div key={insp.inspector_name} className="flex items-center justify-between px-3 py-1.5 bg-gray-100 rounded">
                <span className="text-xs text-gray-800 font-medium">{insp.inspector_name}</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {insp.external > 0 && <span className="text-[10px] text-[#2F5D8A]">EXT:{insp.external}</span>}
                    {insp.internal > 0 && <span className="text-[10px] text-[#B08A3E]">INT:{insp.internal}</span>}
                    {insp.regular > 0 && <span className="text-[10px] text-[#2F5D8A]">REG:{insp.regular}</span>}
                  </div>
                  <span className="text-xs font-medium text-[#6B8F62]">$ {insp.total_amount.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const WeeklyView = ({ weeklyData }) => {
  if (!weeklyData) {
    return (
      <div className="flex items-center justify-center h-20">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D1A054]" />
      </div>
    );
  }

  const fmtDate = (d) => {
    const dt = new Date(d + "T12:00:00");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const fmtDateFull = (d) => {
    const dt = new Date(d + "T12:00:00");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="space-y-4" data-testid="transmission-weekly">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Closing cycle: Tuesday - Monday</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Grand Total:</span>
          <span className="text-lg font-bold text-[#6B8F62]" data-testid="transmission-grand-total">$ {weeklyData.grand_total.toFixed(2)}</span>
        </div>
      </div>

      {weeklyData.weeks.map((week, wi) => (
        <div key={wi} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`weekly-period-${week.period_start}`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white/80">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-[#B08A3E]" />
              <div>
                <p className="text-sm font-medium text-[#1F2937]" data-testid={`weekly-period-label-${wi}`}>
                  {fmtDate(week.period_start)} - {fmtDate(week.period_end)}
                </p>
                <p className="text-[10px] text-gray-400 uppercase">
                  Payment: <span className="text-[#B08A3E] font-medium">{fmtDateFull(week.payment_date)}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-400">{week.total_orders} orders</span>
              <span className="text-sm font-bold text-[#6B8F62]" data-testid={`weekly-period-total-${wi}`}>$ {week.period_total.toFixed(2)}</span>
            </div>
          </div>

          {week.inspectors.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Inspector</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">External</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Internal</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Regular</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Total</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {week.inspectors.map((insp) => (
                  <tr key={insp.inspector_name} className="hover:bg-gray-50" data-testid={`weekly-row-${insp.inspector_name}`}>
                    <td className="px-4 py-2 text-gray-800 font-medium">{insp.inspector_name}</td>
                    <td className="px-4 py-2 text-center text-[#2F5D8A]">{insp.external}</td>
                    <td className="px-4 py-2 text-center text-[#B08A3E]">{insp.internal}</td>
                    <td className="px-4 py-2 text-center text-[#2F5D8A]">{insp.regular}</td>
                    <td className="px-4 py-2 text-center text-gray-800 font-medium">{insp.total_orders}</td>
                    <td className="px-4 py-2 text-right text-[#6B8F62] font-bold">$ {insp.total_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-4 text-center text-gray-300 text-sm">No transmissions in this period</div>
          )}
        </div>
      ))}
    </div>
  );
};

const RatesView = ({ editRates, setEditRates, onSave, saving }) => (
  <div className="max-w-md space-y-4" data-testid="transmission-rates">
    <p className="text-sm text-gray-500">Set payment rate per transmission type (USD)</p>
    {["external", "internal", "regular"].map((key) => (
      <div key={key}>
        <label className="block text-xs text-gray-500 mb-1 uppercase font-medium">{key} Rate</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={editRates[key]}
            onChange={(e) => setEditRates({ ...editRates, [key]: parseFloat(e.target.value) || 0 })}
            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none"
            data-testid={`rate-input-${key}`}
          />
        </div>
      </div>
    ))}
    <button
      onClick={onSave}
      disabled={saving}
      className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] font-semibold rounded-lg text-sm disabled:opacity-50"
      data-testid="save-rates-btn"
    >
      {saving ? "Saving..." : "Save Rates"}
    </button>
  </div>
);

// ──── Office-Paid Transmission Report ────
const OfficePaidView = ({ officeData }) => {
  if (!officeData) return <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>;
  const { records, total_count, total_value } = officeData;

  return (
    <div className="space-y-4" data-testid="office-paid-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-[#D1A054]/35 p-3 text-center">
          <p className="text-2xl font-bold text-[#9A7A3E]" data-testid="office-total-count">{total_count}</p>
          <p className="text-xs text-gray-500 uppercase">Total Office-Paid</p>
        </div>
        <div className="bg-white rounded-lg border border-[#D1A054]/35 p-3 text-center">
          <p className="text-2xl font-bold text-[#9A7A3E]" data-testid="office-total-value">${total_value.toFixed(2)}</p>
          <p className="text-xs text-gray-500 uppercase">Total Cost</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <p className="text-2xl font-bold text-gray-600">{total_count > 0 ? (total_value / total_count).toFixed(2) : '0.00'}</p>
          <p className="text-xs text-gray-500 uppercase">Avg per Order</p>
        </div>
      </div>

      {/* Report */}
      {records.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-lg border border-gray-200">
          No office-paid transmissions in the last 30 days
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Order #</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Company</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Type</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Flow</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Address</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Inspector</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Transmitted By</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold uppercase">Reason</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 font-semibold uppercase">Value</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-100/20" data-testid={`office-record-${idx}`}>
                    <td className="px-3 py-2 text-[#1F2937] font-mono text-xs">{r.order_number}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.company}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.order_type}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.flow_type === 'External' ? 'bg-[#2F5D8A]/15 text-[#2F5D8A]' : r.flow_type === 'Internal' ? 'bg-[#6B5B8D]/15 text-[#6B5B8D]' : 'bg-gray-200 text-gray-600'}`}>{r.flow_type}</span></td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{r.address}{r.city ? `, ${r.city}` : ''}{r.state ? `, ${r.state}` : ''}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.inspector_name}</td>
                    <td className="px-3 py-2 text-[#B08A3E] text-xs font-medium">{r.transmitted_by}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{r.date}</td>
                    <td className="px-3 py-2 text-[#9A7A3E] text-xs">{r.office_reason}{r.office_reason_detail ? ` — ${r.office_reason_detail}` : ''}</td>
                    <td className="px-3 py-2 text-right text-[#1F2937] font-medium text-xs">${r.transmission_value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-300 bg-gray-50/50">
                  <td colSpan="9" className="px-3 py-2 text-right text-xs text-gray-500 font-semibold uppercase">Total</td>
                  <td className="px-3 py-2 text-right text-[#9A7A3E] font-bold">${total_value.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransmissionPage;
